/**
 * RetentionPoliciesPage — System · Retention Policies.
 *
 * How long the bank keeps each kind of record before purge, archive, or
 * cold-storage. The retention scheduler (Function timer) reads this catalog
 * nightly to compute purge dates for completed cases. Composite primary key
 * on (PolicyCode, CategoryCode); the read endpoint also returns the allowed
 * RetentionCategory catalog so the editor offers a dropdown instead of raw
 * text input.
 *
 * Contract (verified 2026-06-16 against primary sources — entity/service
 * field names match the wire exactly; every field persists on insert + update):
 *   - Entity:   PCBJxChangeApi/Data/Entities/Config/Codes/RetentionPolicy.cs
 *               (PolicyCode/CategoryCode/RetentionMonths/AllowHold/PurgeAction/
 *                Notes/IsActive + audit cols the FE ignores)
 *   - Catalog:  RetentionCategory.cs (Code/Name/Description/IsActive)
 *   - Read:     ConfigFunction.cs:876 GET /v3/config/retention-policies
 *               returns { success:true, data:{ policies, categories } } —
 *               raw entity rows, NOT a projection, so wire == entity camelCase.
 *               The read does NOT filter WHERE IsActive (returns all rows
 *               ordered by code) → delete is therefore SOFT (isActive=false),
 *               keeping IsActive editable, even though the service DELETE is hard.
 *   - Write:    AdminNotificationsFunction.cs:155 POST /v3/admin/retention-policies
 *               (AdminNotificationsService.UpsertRetentionPolicyAsync) — every
 *               one of the 7 fields persists; no silent drop.
 *   - Delete:   AdminNotificationsFunction.cs:212
 *               DELETE /v3/admin/retention-policies/{policyCode}/{categoryCode}
 *               (used here only via soft-delete upsert path; hard route unused).
 *
 * No typed api-client method exists for retention policies, so this page uses
 * the raw client.http.post / client.http.get against the routes above.
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: MSAL bearer via ADMIN_AUTH_TOKEN_SENTINEL (no bypass-key prompt;
 * bypass-key gated OFF in prod). RequireBypassAsync → AdminAuthService.Authorize
 * accepts the Admin-or-higher bearer token the sentinel headers carry.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

type PurgeAction = 'Delete' | 'Archive' | 'ColdStorage';

interface PolicyRow {
  policyCode: string;
  categoryCode: string;
  retentionMonths: number;
  allowHold: boolean;
  purgeAction: PurgeAction | string;
  notes: string | null;
  isActive: boolean;
}

interface CategoryRow {
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

interface RetentionPayload {
  policies: ReadonlyArray<PolicyRow>;
  categories: ReadonlyArray<CategoryRow>;
}

interface EditDraft extends PolicyRow {
  isNew: boolean;
}

interface AdminWriteResult {
  success: boolean;
  data?: unknown;
  error?: string;
  code?: string;
}

const PURGE_ACTIONS: ReadonlyArray<{ value: PurgeAction; label: string }> = [
  { value: 'Delete', label: 'Delete — permanently remove (non-evidence records)' },
  { value: 'Archive', label: 'Archive — cheap slow storage, still retrievable' },
  { value: 'ColdStorage', label: 'Cold Storage — encrypted offline media, archival evidence' },
];

function emptyDraft(firstCategory: string): EditDraft {
  return {
    policyCode: '',
    categoryCode: firstCategory,
    retentionMonths: 60,
    allowHold: true,
    purgeAction: 'Archive',
    notes: '',
    isActive: true,
    isNew: true,
  };
}

function fmtMonths(months: number): string {
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  if (months % 12 === 0) {
    const y = months / 12;
    return `${y} year${y === 1 ? '' : 's'}`;
  }
  const y = Math.floor(months / 12);
  const m = months % 12;
  return `${y}y ${m}m`;
}

function draftToBody(d: EditDraft): Record<string, unknown> {
  return {
    policyCode: d.policyCode.trim(),
    categoryCode: d.categoryCode.trim(),
    retentionMonths: d.retentionMonths,
    allowHold: d.allowHold,
    purgeAction: d.purgeAction,
    notes: d.notes && d.notes.trim() ? d.notes.trim() : null,
    isActive: d.isActive,
  };
}

export function RetentionPoliciesPage(): JSX.Element {
  const client = useApiClient();
  const [data, setData] = useState<RetentionPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PolicyRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setData(null);
      setLoadError(null);
      try {
        const resp = await client.http.get<{
          success: boolean;
          data?: RetentionPayload;
          error?: string;
        }>('/v3/config/retention-policies');
        if (cancelled) return;
        if (resp.success && resp.data) setData(resp.data);
        else setLoadError(resp.error ?? 'Failed to load retention policies.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const categoryByCode = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of data?.categories ?? []) m.set(c.code, c);
    return m;
  }, [data]);

  const categoryOptions = useMemo(
    () =>
      (data?.categories ?? [])
        .filter((c) => c.isActive)
        .map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
    [data],
  );

  const filtered = useMemo(() => {
    if (!data) return null;
    const n = filter.trim().toLowerCase();
    const sorted = [...data.policies].sort((a, b) => {
      if (a.policyCode !== b.policyCode) return a.policyCode.localeCompare(b.policyCode);
      return a.categoryCode.localeCompare(b.categoryCode);
    });
    if (!n) return sorted;
    return sorted.filter((p) => {
      const cat = categoryByCode.get(p.categoryCode);
      const hay = `${p.policyCode} ${p.categoryCode} ${cat?.name ?? ''} ${p.notes ?? ''}`.toLowerCase();
      return hay.includes(n);
    });
  }, [data, filter, categoryByCode]);

  // Single write path: upsert. Soft-delete reuses it with isActive=false because
  // the read endpoint returns inactive rows too (no WHERE IsActive filter).
  const upsert = useCallback(
    async (body: Record<string, unknown>): Promise<boolean> => {
      const resp = await client.http.post<AdminWriteResult>('/v3/admin/retention-policies', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body,
      });
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.policyCode.trim() || !draft.categoryCode.trim()) {
      setSaveError('Policy code and category are both required.');
      return;
    }
    if (draft.retentionMonths < 1) {
      setSaveError('Retention must be at least 1 month.');
      return;
    }
    if (!draft.purgeAction.trim()) {
      setSaveError('An on-expiry action is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draftToBody(draft));
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created ${draft.policyCode}/${draft.categoryCode}.`
          : `Updated ${draft.policyCode}/${draft.categoryCode}.`,
      });
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-delete: the config read returns inactive rows, so flipping IsActive
      // hides the row from downstream consumers while preserving audit history.
      await upsert(draftToBody({ ...deleteTarget, isActive: false, isNew: false }));
      setToast({
        tone: 'warn',
        text: `${deleteTarget.policyCode}/${deleteTarget.categoryCode} marked inactive.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<PolicyRow>> = useMemo(
    () => [
      { header: 'Policy', cell: (r) => <code className="cell-mono--bold">{r.policyCode}</code> },
      {
        header: 'Category',
        cell: (r) => <span className="cell-strong">{categoryByCode.get(r.categoryCode)?.name ?? r.categoryCode}</span>,
      },
      { header: 'Retention', cell: (r) => <span className="cell-mono">{fmtMonths(r.retentionMonths)}</span> },
      { header: 'On expiry', cell: (r) => r.purgeAction },
      {
        header: 'Allows hold',
        cell: (r) => (r.allowHold ? <Pill tone="info">Yes</Pill> : <Pill tone="muted">No</Pill>),
      },
      {
        header: 'Notes',
        cell: (r) => (
          <span className="cell-desc" title={r.notes ?? undefined}>
            {r.notes ?? '—'}
          </span>
        ),
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft({ ...r, notes: r.notes ?? '', isNew: false }); setSaveError(null); }}>
              Edit
            </Button>
            <Button onClick={() => setDeleteTarget(r)}>Delete</Button>
          </div>
        ),
      },
    ],
    [categoryByCode],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load retention policies: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!data) return <Spinner label="Loading retention policies…" />;

  const noCategories = data.categories.filter((c) => c.isActive).length === 0;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        Retention durations are regulatory requirements (BSA 5 years, ECOA 25 months, etc.) and drive
        automatic purge. Coordinate any change with Compliance and Legal before saving.
      </Banner>

      <Card
        title={`Retention Policies — ${filtered?.length ?? 0} of ${data.policies.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by policy, category, or notes…"
              />
            </div>
            <Button
              variant="primary"
              disabled={noCategories}
              onClick={() => { setDraft(emptyDraft(data.categories.find((c) => c.isActive)?.code ?? '')); setSaveError(null); }}
            >
              + Add Policy
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          How long the bank keeps each kind of record before purge, archive, or cold-storage. The
          retention scheduler reads this catalog every night to compute purge dates for completed cases.
        </p>
        <DataTable<PolicyRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => `${r.policyCode}|${r.categoryCode}`}
          emptyState={<p className="empty">No retention policies match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a retention policy' : `Edit ${draft?.policyCode}/${draft?.categoryCode}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Policy code"
                value={draft.policyCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, policyCode: v.slice(0, 32) })}
                disabled={!draft.isNew}
                monospace
                maxLength={32}
                placeholder="BSA_5YR"
                required
                hint={draft.isNew ? 'Up to 32 characters. Permanent — cannot change once saved.' : 'Permanent — cannot change.'}
              />
              <SelectField
                label="Category"
                value={draft.categoryCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, categoryCode: v })}
                options={categoryOptions}
                placeholder="— Select category —"
                disabled={!draft.isNew}
                required
                hint="Which kind of record this policy governs."
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Retention (months)"
                value={draft.retentionMonths}
                onChange={(v) => setDraft({ ...draft, retentionMonths: Math.max(1, v ?? 1) })}
                min={1}
                placeholder="60"
                required
                hint="How long records persist before the on-expiry action fires."
              />
              <SelectField
                label="On expiry"
                value={draft.purgeAction}
                onChange={(v) => setDraft({ ...draft, purgeAction: v })}
                options={PURGE_ACTIONS}
                required
                hint="What happens to records when retention ends."
              />
            </div>

            <BoolField
              label="Allow legal hold"
              value={draft.allowHold}
              onChange={(v) => setDraft({ ...draft, allowHold: v })}
              hint="Active legal holds pause expiry for matching records."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive policies are skipped by the retention scheduler."
            />

            <TextArea
              label="Notes (regulatory citation or context)"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v || null })}
              rows={2}
              placeholder="e.g. 12 CFR 1020.430 — BSA recordkeeping; 5 years from account close"
            />

            <Banner tone="info">
              Records of category <strong>{draft.categoryCode || '—'}</strong> are kept for{' '}
              <strong>{fmtMonths(draft.retentionMonths)}</strong>, then{' '}
              {draft.purgeAction === 'Delete'
                ? 'permanently deleted.'
                : draft.purgeAction === 'Archive'
                  ? 'archived to long-term storage.'
                  : 'moved to encrypted cold storage.'}{' '}
              {draft.allowHold ? 'Active legal holds pause expiry.' : 'Holds do not apply.'}
            </Banner>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this policy inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void confirmDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark{' '}
              <code className="cell-mono">{deleteTarget.policyCode}/{deleteTarget.categoryCode}</code>{' '}
              inactive. Existing records under this policy keep their original retention; new records
              will skip the policy. (Soft delete — the row is preserved for audit history.)
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default RetentionPoliciesPage;
